import { addEntity, addComponent } from 'bitecs';
import { Position, Animal, AI, AI_STATE, ANIMAL_TYPES, Player, Plant, PLANT_TYPES, BIOME_TYPES } from '../world.js';
import { TILE_SIZE, MAX_ENTITIES } from '../config.js';
import { tileManager, TILE_TYPES } from '../systems/tileManager.js';

// Global app reference for rendering
let app = null;

export function setApp(appInstance) {
    app = appInstance;
}

/**
 * Create a new rabbit entity
 * @param {object} world - The game world
 * @param {number} x - X position in world coordinates
 * @param {number} y - Y position in world coordinates
 * @returns {number} - The entity ID of the created rabbit
 */
export function createRabbit(world, x, y) {
    // Convert world coordinates to tile coordinates
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);
    
    // Check if the tile is land
    const tile = tileManager.getTile(tileX, tileY);
    if (!tile || tile.type !== TILE_TYPES.LAND) {
        console.log('[RABBIT] Cannot spawn rabbit at position:', x, y, '- not on land');
        return null;
    }
    
    console.log('[RABBIT] Creating rabbit at position:', x, y);
    
    // Create entity
    const rabbit = addEntity(world);
    
    // Add required components
    addComponent(world, Animal, rabbit);
    addComponent(world, AI, rabbit);
    addComponent(world, Position, rabbit);
    
    // Initialize rabbit properties
    Animal.type[rabbit] = ANIMAL_TYPES.RABBIT.id;
    Animal.x[rabbit] = x;
    Animal.y[rabbit] = y;
    Position.x[rabbit] = x;
    Position.y[rabbit] = y;
    
    // Set random age between 0-100
    Animal.age[rabbit] = Math.floor(Math.random() * 100);
    
    // Set initial weight
    Animal.weight[rabbit] = 10;
    
    // Movement state
    Animal.isMoving[rabbit] = 1; // Start moving immediately
    
    // Energy management
    Animal.energy[rabbit] = 20; // Start with a fixed energy level
    Animal.lastEnergyUpdate[rabbit] = world.tick;
    
    // Fleeing behavior
    Animal.fleeTimer[rabbit] = 0;
    Animal.isFleeing[rabbit] = 0;
    
    // Initialize AI state to MOVING with random target
    AI.state[rabbit] = AI_STATE.MOVING;
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * 8 * TILE_SIZE;
    AI.targetX[rabbit] = x + Math.cos(angle) * distance;
    AI.targetY[rabbit] = y + Math.sin(angle) * distance;
    Animal.isMoving[rabbit] = 1;
    AI.timer[rabbit] = Math.floor(Math.random() * 1000);
    
    // Add to world entities
    world.entities.push(rabbit);
    
    console.log('[RABBIT] Rabbit created with ID:', rabbit, 'State:', AI_STATE[AI.state[rabbit]], 'Target:', AI.targetX[rabbit], AI.targetY[rabbit]);
    return rabbit;
}

// Helper function to check if a position is on land
const isPositionOnLand = (x, y) => {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);
    const tile = tileManager.getTile(tileX, tileY);
    return tile && tile.type === TILE_TYPES.LAND;
};

// Helper function to find a valid land position
const findValidLandPosition = (startX, startY, maxAttempts = 8) => {
    // Try closer positions first (like Don't Starve rabbits)
    const distances = [TILE_SIZE * 0.5, TILE_SIZE * 1, TILE_SIZE * 1.5];
    for (const distance of distances) {
        for (let i = 0; i < maxAttempts; i++) {
            const angle = (i / maxAttempts) * Math.PI * 2;
            const testX = startX + Math.cos(angle) * distance;
            const testY = startY + Math.sin(angle) * distance;
            if (isPositionOnLand(testX, testY)) {
                return { x: testX, y: testY };
            }
        }
    }
    return null;
};

// Helper function to find the nearest player
const findNearestPlayer = (world, rabbitX, rabbitY) => {
    const players = world.queries.players(world);
    if (players.length === 0) return null;
    
    let nearestPlayer = null;
    let minDistance = Infinity;
    
    for (const player of players) {
        const dx = Position.x[player] - rabbitX;
        const dy = Position.y[player] - rabbitY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < minDistance) {
            minDistance = distance;
            nearestPlayer = player;
        }
    }
    
    return { player: nearestPlayer, distance: minDistance };
};

// Helper function to get a random hop direction, optionally away from a threat
const getRandomHopDirection = (currentX, currentY, threatX = null, threatY = null) => {
    let angle;
    
    if (threatX !== null && threatY !== null) {
        // Calculate angle away from threat
        const dx = currentX - threatX;
        const dy = currentY - threatY;
        const baseAngle = Math.atan2(dy, dx); // Direction away from threat
        
        // Add some randomness to the fleeing direction (±45 degrees)
        angle = baseAngle + (Math.random() - 0.5) * Math.PI / 2;
    } else {
        // No threat, just hop in a random direction
        angle = Math.random() * Math.PI * 2;
    }
    
    // Hop farther when fleeing from a threat
    const hopDistance = threatX !== null ? 
        TILE_SIZE * (1.0 + Math.random() * 0.5) : // Longer hop when fleeing (1.0-1.5 tiles)
        TILE_SIZE * (0.5 + Math.random() * 0.5); // Normal hop (0.5-1.0 tiles)
    
    return {
        x: currentX + Math.cos(angle) * hopDistance,
        y: currentY + Math.sin(angle) * hopDistance
    };
};

// Helper function to find the nearest bush
const findNearestBush = (world, rabbitX, rabbitY) => {
    const plants = world.queries.plants(world);
    console.log(`[RABBIT] Checking ${plants.length} plants for bushes`);
    
    let nearestBush = null;
    let minDistance = Infinity;
    
    for (const plant of plants) {
        // Debug log plant type and food amount
        console.log(`[RABBIT] Plant ${plant}: type=${Plant.type[plant]}, food=${Plant.foodAmount[plant]}`);
        
        // Check if it's a bush and has food
        if (Plant.type[plant] === 1 && Plant.foodAmount[plant] > 0) { // BUSH.id is 1
            const dx = Position.x[plant] - rabbitX;
            const dy = Position.y[plant] - rabbitY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            console.log(`[RABBIT] Found bush at distance ${distance.toFixed(2)} tiles`);
            
            if (distance < minDistance) {
                minDistance = distance;
                nearestBush = plant;
            }
        }
    }
    
    if (nearestBush) {
        console.log(`[RABBIT] Selected nearest bush at distance ${minDistance.toFixed(2)} tiles`);
    } else {
        console.log('[RABBIT] No bushes found with food');
    }
    
    return nearestBush ? { plant: nearestBush, distance: minDistance } : null;
};

// Helper function to eat from a bush
function eatFromBush(world, rabbit, bush) {
    if (!bush || !Plant.foodAmount[bush] || Plant.foodAmount[bush] <= 0) {
        return false;
    }
    
    // Calculate how much food to consume
    const maxFoodToConsume = Math.min(Plant.foodAmount[bush], 10);
    const actualFoodConsumed = maxFoodToConsume;
    
    // Reduce food amount in the bush
    Plant.foodAmount[bush] -= actualFoodConsumed;
    
    // Convert food to energy based on efficiency - increased multiplier since this is now a supplemental boost
    const energyGained = actualFoodConsumed * ANIMAL_TYPES.RABBIT.energyPerFood * ANIMAL_TYPES.RABBIT.energyEfficiency * 2;
    
    // Add energy with clamping to max value
    const maxEnergy = ANIMAL_TYPES.RABBIT.maxEnergy || 100;
    Animal.energy[rabbit] = Math.min(maxEnergy, Animal.energy[rabbit] + energyGained);
    
    console.log(`[RABBIT] Ate ${actualFoodConsumed.toFixed(2)} food from bush, gained ${energyGained.toFixed(2)} energy boost`);
    return true;
};

// Helper function to get a random search direction
const getRandomSearchDirection = (currentX, currentY) => {
    const angle = Math.random() * Math.PI * 2;
    const distance = TILE_SIZE * (1 + Math.random() * 2); // Search 1-3 tiles away
    return {
        x: currentX + Math.cos(angle) * distance,
        y: currentY + Math.sin(angle) * distance
    };
};

/**
 * Update rabbit behavior
 * @param {object} world - The game world
 * @param {number} rabbit - The rabbit entity ID
 * @param {number} delta - Time since last update
 */
export function updateRabbit(world, rabbit, delta) {
    // Get current state
    const currentState = AI.state[rabbit];
    const currentX = Position.x[rabbit];
    const currentY = Position.y[rabbit];
    
    // Constants for behavior
    const FLEE_DISTANCE = TILE_SIZE * 1.5;
    const CAUTION_DISTANCE = TILE_SIZE * 3;
    const REPRODUCTION_INTERVAL = 2000; // Ticks between reproduction attempts
    const REPRODUCTION_RADIUS = TILE_SIZE * 3; // Distance to look for other rabbits
    const MAX_RABBITS = 50; // Cap the total rabbit population
    
    // ===== ENERGY MANAGEMENT =====
    // Simplified energy - rabbits always maintain a minimum energy level
    // Just ensure energy doesn't fall below 50% of max
    if (Animal.energy[rabbit] < 50) {
        Animal.energy[rabbit] = 50 + Math.random() * 20; // Ensure 50-70 energy minimum
    }
    
    // Occasionally increase energy if on grass (simplified grazing)
    if (Math.random() < 0.3) {
        const tileX = Math.floor(currentX / TILE_SIZE);
        const tileY = Math.floor(currentY / TILE_SIZE);
        const tile = tileManager.getTile(tileX, tileY);
        
        if (tile && tile.type === TILE_TYPES.LAND && 
            (tile.biome === BIOME_TYPES.PLAINS || tile.biome === BIOME_TYPES.FOREST)) {
            // Simple energy gain on grass - capped at max
            Animal.energy[rabbit] = Math.min(100, Animal.energy[rabbit] + 5);
        }
    }
    
    // ===== AGING =====
    // Increment age but don't let rabbits die of old age
    Animal.age[rabbit] += 1;
    
    // ===== REPRODUCTION =====
    // Attempt reproduction periodically when energy is good
    const canReproduce = Animal.energy[rabbit] > 75 && // Energy threshold
                        Animal.age[rabbit] > 500 && // Maturity threshold
                        world.tick % REPRODUCTION_INTERVAL === rabbit % REPRODUCTION_INTERVAL; // Stagger timing
    
    if (canReproduce) {
        // Count existing rabbits before attempting reproduction
        const existingRabbits = world.entities.filter(eid => 
            Animal.type && Animal.type[eid] === ANIMAL_TYPES.RABBIT.id
        ).length;
        
        if (existingRabbits < MAX_RABBITS) {
            // Find nearby rabbit for reproduction
            const nearbyRabbits = world.entities.filter(eid => 
                eid !== rabbit && // Not self
                Animal.type && Animal.type[eid] === ANIMAL_TYPES.RABBIT.id && // Is rabbit
                Animal.energy[eid] > 75 && // Has energy
                Math.abs(Position.x[eid] - currentX) < REPRODUCTION_RADIUS && // Within range
                Math.abs(Position.y[eid] - currentY) < REPRODUCTION_RADIUS
            );
            
            if (nearbyRabbits.length > 0) {
                // Reproduce - create baby rabbit nearby
                const offset = TILE_SIZE * 0.5;
                const babyX = currentX + (Math.random() * offset * 2 - offset);
                const babyY = currentY + (Math.random() * offset * 2 - offset);
                
                if (isPositionOnLand(babyX, babyY)) {
                    const babyRabbit = createRabbit(world, babyX, babyY);
                    if (babyRabbit) {
                        // Initialize baby rabbit with young age
                        Animal.age[babyRabbit] = 0;
                        Animal.energy[babyRabbit] = 70;
                        console.log(`[RABBIT] New baby rabbit born at (${babyX.toFixed(1)}, ${babyY.toFixed(1)})!`);
                        
                        // Parents lose some energy from reproduction
                        Animal.energy[rabbit] -= 20;
                        Animal.energy[nearbyRabbits[0]] -= 20;
                    }
                }
            }
        }
    }
    
    // ===== PLAYER INTERACTION =====
    // Check for nearby player
    const playerInfo = findNearestPlayer(world, currentX, currentY);
    
    // Handle player proximity - flee if too close
    if (playerInfo && playerInfo.distance < FLEE_DISTANCE) {
        makeRabbitFlee(world, rabbit, Position.x[playerInfo.player], Position.y[playerInfo.player]);
    } else if (playerInfo && playerInfo.distance < CAUTION_DISTANCE && Math.random() < 0.3) {
        // Cautiously move away if player is nearby
        const hopDir = getRandomHopDirection(currentX, currentY, Position.x[playerInfo.player], Position.y[playerInfo.player]);
        if (isPositionOnLand(hopDir.x, hopDir.y)) {
            AI.targetX[rabbit] = hopDir.x;
            AI.targetY[rabbit] = hopDir.y;
            AI.state[rabbit] = AI_STATE.MOVING;
            Animal.isMoving[rabbit] = 1;
        }
    }
    
    // Handle different AI states
    switch (currentState) {
        case AI_STATE.IDLE:
            console.log('[RABBIT] Entered IDLE state.'); // Log when entering IDLE
            // More likely to start moving (70% chance)
            if (Math.random() < 0.7) {
                console.log('[RABBIT] IDLE: Decided to move.'); // Log decision to move
                const hopDir = getRandomHopDirection(currentX, currentY);
                if (isPositionOnLand(hopDir.x, hopDir.y)) {
                    AI.targetX[rabbit] = hopDir.x;
                    AI.targetY[rabbit] = hopDir.y;
                    AI.state[rabbit] = AI_STATE.MOVING;
                    Animal.isMoving[rabbit] = 1;
                    console.log('[RABBIT] IDLE: Transitioning to MOVING state. Target:', AI.targetX[rabbit], AI.targetY[rabbit]);
                } else {
                     console.log('[RABBIT] IDLE: Hop target not on land, staying IDLE.');
                }
            } else {
                 console.log('[RABBIT] IDLE: Decided to stay idle.');
            }
            break;
            
        case AI_STATE.MOVING:
            // Check if target is on land
            if (!isPositionOnLand(AI.targetX[rabbit], AI.targetY[rabbit])) {
                const hopDir = getRandomHopDirection(currentX, currentY);
                if (isPositionOnLand(hopDir.x, hopDir.y)) {
                    AI.targetX[rabbit] = hopDir.x;
                    AI.targetY[rabbit] = hopDir.y;
                } else {
                    AI.state[rabbit] = AI_STATE.IDLE;
                    Animal.isMoving[rabbit] = 0;
                    break;
                }
            }
            
            // Move towards target
            const dx = AI.targetX[rabbit] - currentX;
            const dy = AI.targetY[rabbit] - currentY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            
            if (dist < 0.5) {
                // Reached target - find new target
                const hopDir = getRandomHopDirection(currentX, currentY);
                if (isPositionOnLand(hopDir.x, hopDir.y)) {
                    AI.targetX[rabbit] = hopDir.x;
                    AI.targetY[rabbit] = hopDir.y;
                    Animal.isMoving[rabbit] = 1;
                } else {
                    AI.state[rabbit] = AI_STATE.IDLE;
                    Animal.isMoving[rabbit] = 0;
                }
            } else {
                // Calculate next position with hopping movement
                const baseSpeed = ANIMAL_TYPES.RABBIT.speed * 4;
                const speedMultiplier = (playerInfo && playerInfo.distance < FLEE_DISTANCE) ? 1.0 : 0.5;
                const speed = baseSpeed * speedMultiplier * (Animal.isFleeing[rabbit] ? 2 : 1);
                
                // Add slight randomness to movement for more natural hopping
                const randomAngle = (Math.random() - 0.5) * 0.2;
                const moveAngle = Math.atan2(dy, dx) + randomAngle;
                const moveX = Math.cos(moveAngle) * speed;
                const moveY = Math.sin(moveAngle) * speed;
                
                const nextX = currentX + moveX;
                const nextY = currentY + moveY;
                
                // Only move if next position is on land
                if (isPositionOnLand(nextX, nextY)) {
                    Position.x[rabbit] = nextX;
                    Position.y[rabbit] = nextY;
                    Animal.x[rabbit] = nextX;
                    Animal.y[rabbit] = nextY;
                } else {
                    // If next position is not on land, find new target
                    const hopDir = getRandomHopDirection(currentX, currentY);
                    if (isPositionOnLand(hopDir.x, hopDir.y)) {
                        AI.targetX[rabbit] = hopDir.x;
                        AI.targetY[rabbit] = hopDir.y;
                    } else {
                        AI.state[rabbit] = AI_STATE.IDLE;
                        Animal.isMoving[rabbit] = 0;
                    }
                }
            }
            break;
            
        case AI_STATE.SEARCHING:
            // Check if we've reached the search target
            const searchDx = AI.targetX[rabbit] - currentX;
            const searchDy = AI.targetY[rabbit] - currentY;
            const searchDist = Math.sqrt(searchDx * searchDx + searchDy * searchDy);
            
            if (searchDist < 0.5) {
                // Look for bushes at current position
                const nearbyBush = findNearestBush(world, currentX, currentY);
                if (nearbyBush && nearbyBush.distance < SEARCH_RADIUS) {
                    // Found a bush, move towards it
                    AI.targetX[rabbit] = Position.x[nearbyBush.plant];
                    AI.targetY[rabbit] = Position.y[nearbyBush.plant];
                    AI.state[rabbit] = AI_STATE.MOVING;
                } else {
                    // No bush found, continue searching
                    const searchDir = getRandomSearchDirection(currentX, currentY);
                    if (isPositionOnLand(searchDir.x, searchDir.y)) {
                        AI.targetX[rabbit] = searchDir.x;
                        AI.targetY[rabbit] = searchDir.y;
                    } else {
                        AI.state[rabbit] = AI_STATE.IDLE;
                        Animal.isMoving[rabbit] = 0;
                    }
                }
            } else {
                // Move towards search target
                const baseSpeed = ANIMAL_TYPES.RABBIT.speed * 3; // Slightly slower when searching
                const moveAngle = Math.atan2(searchDy, searchDx);
                const moveX = Math.cos(moveAngle) * baseSpeed;
                const moveY = Math.sin(moveAngle) * baseSpeed;
                
                const nextX = currentX + moveX;
                const nextY = currentY + moveY;
                
                if (isPositionOnLand(nextX, nextY)) {
                    Position.x[rabbit] = nextX;
                    Position.y[rabbit] = nextY;
                    Animal.x[rabbit] = nextX;
                    Animal.y[rabbit] = nextY;
                } else {
                    // If next position is not on land, find new search target
                    const searchDir = getRandomSearchDirection(currentX, currentY);
                    if (isPositionOnLand(searchDir.x, searchDir.y)) {
                        AI.targetX[rabbit] = searchDir.x;
                        AI.targetY[rabbit] = searchDir.y;
                    } else {
                        AI.state[rabbit] = AI_STATE.IDLE;
                        Animal.isMoving[rabbit] = 0;
                    }
                }
            }
            break;
            
        case AI_STATE.EATING:
            // Get target bush ID (stored in timer field)
            const targetBushId = AI.timer[rabbit];

            // Check if bush ID is valid and exists
            if (targetBushId === undefined || Plant.type[targetBushId] === undefined) {
                console.log('[RABBIT] Eating target bush is invalid or destroyed. Returning to IDLE.');
                AI.state[rabbit] = AI_STATE.IDLE;
                Animal.isMoving[rabbit] = 0;
                break;
            }

            // Check distance to bush
            const bushX = Position.x[targetBushId];
            const bushY = Position.y[targetBushId];
            const dxEat = bushX - currentX;
            const dyEat = bushY - currentY;
            const distToBush = Math.sqrt(dxEat * dxEat + dyEat * dyEat);

            if (distToBush > BUSH_EATING_DISTANCE * 1.1) { // Allow a small tolerance
                console.log('[RABBIT] Too far from bush while eating. Returning to IDLE.');
                AI.state[rabbit] = AI_STATE.IDLE;
                Animal.isMoving[rabbit] = 0;
                break;
            }

            // Attempt to eat
            if (eatFromBush(world, rabbit, targetBushId)) {
                // Check if energy is full
                if (Animal.energy[rabbit] >= ANIMAL_TYPES.RABBIT.dailyEnergyRequirement) {
                    console.log('[RABBIT] Energy full after eating. Returning to IDLE.');
                    AI.state[rabbit] = AI_STATE.IDLE;
                    Animal.isMoving[rabbit] = 0;
                } else {
                    // Continue eating (stay in EATING state)
                    console.log(`[RABBIT] Still eating bush ${targetBushId}, current energy: ${Animal.energy[rabbit].toFixed(2)}`);
                }
            } else {
                // Failed to eat (bush likely empty)
                console.log(`[RABBIT] Bush ${targetBushId} is empty or cannot eat. Returning to IDLE.`);
                AI.state[rabbit] = AI_STATE.IDLE;
                Animal.isMoving[rabbit] = 0;
            }
            break; // End of EATING case
            
        case AI_STATE.RESTING:
            // Regain energy while resting
            Animal.energy[rabbit] = Math.min(
                ANIMAL_TYPES.RABBIT.dailyEnergyRequirement,
                Animal.energy[rabbit] + ANIMAL_TYPES.RABBIT.dailyEnergyRequirement / (24 * 60 * 60) * delta
            );
            
            // Return to IDLE state when energy is high enough
            if (Animal.energy[rabbit] >= ANIMAL_TYPES.RABBIT.dailyEnergyRequirement * 0.7) {
                AI.state[rabbit] = AI_STATE.IDLE;
                Animal.isMoving[rabbit] = 0; // Stop moving while in IDLE
            }
            break;
    }
    
    // Update AI timer
    AI.timer[rabbit]++;
}

/**
 * Make a rabbit flee from a threat
 * @param {object} world - The game world
 * @param {number} rabbit - The rabbit entity ID
 * @param {number} threatX - X position of the threat
 * @param {number} threatY - Y position of the threat
 */
export function makeRabbitFlee(world, rabbit, threatX, threatY) {
    // Use our improved getRandomHopDirection function to get a flee direction
    const fleeTarget = getRandomHopDirection(
        Position.x[rabbit],
        Position.y[rabbit],
        threatX,
        threatY
    );
    
    // Make sure the flee target is on land
    if (isPositionOnLand(fleeTarget.x, fleeTarget.y)) {
        // Set the flee target and state
        AI.targetX[rabbit] = fleeTarget.x;
        AI.targetY[rabbit] = fleeTarget.y;
        AI.state[rabbit] = AI_STATE.MOVING;
        Animal.isMoving[rabbit] = 1;
        Animal.isFleeing[rabbit] = 1;
        Animal.fleeTimer[rabbit] = 60; // Flee for slightly longer
    } else {
        // If flee target is not on land, try again with a different angle
        const retryTarget = getRandomHopDirection(
            Position.x[rabbit],
            Position.y[rabbit]
        );
        
        AI.targetX[rabbit] = retryTarget.x;
        AI.targetY[rabbit] = retryTarget.y;
        AI.state[rabbit] = AI_STATE.MOVING;
        Animal.isMoving[rabbit] = 1;
        Animal.isFleeing[rabbit] = 1;
        Animal.fleeTimer[rabbit] = 30; // Shorter flee time for random direction
    }
    
    console.log(`[RABBIT] Fleeing from threat at (${threatX.toFixed(1)}, ${threatY.toFixed(1)})`);
}